import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { formatDate } from '../../utils';
import { useToast } from '../../contexts/ToastContext';

const EventDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = location.state?.eventId || 1;
  const { showToast } = useToast();

  const [showRegistration, setShowRegistration] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tickets: 1
  });

  const event = {
    id: eventId,
    title: 'Virtual Bridal Expo 2024',
    date: '2024-04-15',
    time: '10:00 AM - 6:00 PM',
    location: 'Online Event',
    type: 'Expo',
    description: 'Join us for the largest South Asian bridal expo of the year! Discover the latest trends, meet top vendors, and get exclusive discounts on wedding services. This virtual event brings together the best wedding professionals from across the country.',
    image: 'https://images.unsplash.com/photo-1519167758481-83f29d8ae8c4?w=800&h=500&fit=crop',
    discount: '20% OFF',
    price: 29.99,
    originalPrice: 49.99,
    featured: true,
    highlights: [
      'Meet 100+ verified wedding vendors',
      'Exclusive discount codes and offers',
      'Live Q&A sessions with industry experts',
      'Virtual booth tours and consultations',
      'Wedding planning workshops',
      'Prize giveaways worth $10,000'
    ],
    schedule: [
      { time: '10:00 AM', title: 'Opening Ceremony & Welcome', speaker: 'Event Host' },
      { time: '10:30 AM', title: 'Wedding Trends 2024 Showcase', speaker: 'Fashion Designers Panel' },
      { time: '12:00 PM', title: 'Vendor Exhibition Opens', speaker: 'All Vendors' },
      { time: '2:00 PM', title: 'Budget Planning Workshop', speaker: 'Financial Expert' },
      { time: '3:30 PM', title: 'Cultural Wedding Traditions', speaker: 'Cultural Consultants' },
      { time: '5:00 PM', title: 'Grand Prize Draw', speaker: 'Event Host' }
    ],
    vendors: 42,
    attendees: 1250,
    rating: 4.8
  };

  const handleRegister = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      showToast('Please fill in all fields', 'warning');
      return;
    }

    showToast(`Registration successful for ${formData.name}! Check your email for event access details.`, 'success', 5000);
    setShowRegistration(false);
    setTimeout(() => {
      navigate('/user-dashboard');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-text-secondary hover:text-primary mb-6 transition-all"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back</span>
          </button>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <span className="bg-purple-500 px-4 py-2 rounded-full text-sm font-medium">
                  {event.type}
                </span>
                {event.featured && (
                  <span className="bg-yellow-500 px-4 py-2 rounded-full text-sm font-medium">
                    <Icon name="Star" size={16} className="inline mr-1" />
                    Featured
                  </span>
                )}
                <span className="bg-red-500 px-4 py-2 rounded-full text-sm font-bold">
                  {event.discount}
                </span>
              </div>
              <h1 className="font-heading font-bold text-4xl mb-2">{event.title}</h1>
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={18} />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={18} />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={18} />
                  <span>{event.location}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h2 className="font-heading font-semibold text-2xl mb-4">About This Event</h2>
                <p className="text-text-secondary leading-relaxed mb-6">{event.description}</p>

                <h3 className="font-semibold text-xl mb-4">Event Highlights</h3>
                <ul className="space-y-3">
                  {event.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-text-secondary">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Schedule */}
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h2 className="font-heading font-semibold text-2xl mb-6">Event Schedule</h2>
                <div className="space-y-4">
                  {event.schedule.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4 pb-4 border-b border-border-light last:border-0">
                      <div className="text-primary font-semibold min-w-[100px]">{item.time}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">{item.title}</h4>
                        <p className="text-sm text-text-secondary">{item.speaker}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-soft p-6 text-center">
                  <Icon name="Users" size={32} className="text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-text-primary">{event.attendees}+</div>
                  <div className="text-sm text-text-secondary">Attendees</div>
                </div>
                <div className="bg-white rounded-xl shadow-soft p-6 text-center">
                  <Icon name="Building" size={32} className="text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-text-primary">{event.vendors}+</div>
                  <div className="text-sm text-text-secondary">Vendors</div>
                </div>
                <div className="bg-white rounded-xl shadow-soft p-6 text-center">
                  <Icon name="Star" size={32} className="text-yellow-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-text-primary">{event.rating}</div>
                  <div className="text-sm text-text-secondary">Rating</div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-primary mb-2">
                    ${event.price}
                  </div>
                  {event.originalPrice && (
                    <div className="text-text-muted line-through text-sm mb-2">
                      ${event.originalPrice}
                    </div>
                  )}
                  <div className="text-sm text-green-600 font-medium">
                    Save ${(event.originalPrice - event.price).toFixed(2)}
                  </div>
                </div>

                {!showRegistration ? (
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    iconName="UserPlus"
                    onClick={() => setShowRegistration(true)}
                  >
                    Register Now
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg mb-2">Registration Details</h3>
                    <p className="text-sm text-text-secondary mb-4">
                      Please fill in your details to complete your registration for this event.
                    </p>
                    <Input
                      label="Full Name"
                      placeholder="Enter your first and last name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                    <Input
                      label="Phone Number"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                    <div>
                      <Input
                        label="Number of Tickets"
                        type="number"
                        min="1"
                        max="10"
                        placeholder="1"
                        value={formData.tickets}
                        onChange={(e) => setFormData({ ...formData, tickets: parseInt(e.target.value) })}
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Maximum 10 tickets per registration
                      </p>
                    </div>
                    <div className="border-t border-border-light pt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-text-secondary">Tickets ({formData.tickets}x)</span>
                        <span className="font-semibold">${(event.price * formData.tickets).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-primary">${(event.price * formData.tickets).toFixed(2)}</span>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      onClick={handleRegister}
                    >
                      Complete Registration
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      onClick={() => setShowRegistration(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-border-light space-y-3">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Share2"
                    onClick={() => showToast('Share functionality coming soon!', 'info')}
                  >
                    Share Event
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Calendar"
                    onClick={() => showToast('Added to calendar!', 'success')}
                  >
                    Add to Calendar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EventDetail;
